import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Checkbox, TextField, Typography } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import _ from "lodash";

// =====================================================
// หาทุกใบไม้ใต้ node
// =====================================================
function leafIds(node) {
  if (!node) return [];

  if (!node.children || node.children.length === 0) {
    return [node.value];
  }

  return node.children.flatMap((c) => leafIds(c));
}

// =====================================================
// flatten tree เพื่อ render
// =====================================================
function flatten(nodes, expanded, depth = 0) {
  return nodes.flatMap((node) => {
    const isExpanded = expanded.includes(node.value);

    return [
      {
        ...node,
        depth,
      },

      ...(node.children && isExpanded
        ? flatten(node.children, expanded, depth + 1)
        : []),
    ];
  });
}

// =====================================================
// หาทุกใบไม้
// =====================================================
function allLeafIds(nodes) {
  let result = [];

  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      result = [...result, ...allLeafIds(node.children)];
    } else {
      result.push(node.value);
    }
  }

  return result;
}

// =====================================================
// หา node จาก id
// =====================================================
function findNodeById(nodes, id) {
  for (const node of nodes) {
    if (node.value === id) {
      return node;
    }

    if (node.children) {
      const found = findNodeById(node.children, id);

      if (found) {
        return found;
      }
    }
  }

  return null;
}

// =====================================================
// Search
// ตอนค้นหาเอาเฉพาะ node ที่ตรงกับ search
// ไม่เอา parent / กลุ่มรถมาแสดง
// =====================================================
function searchNodes(nodes, search) {
  const result = [];

  const walk = (items) => {
    for (const node of items) {
      const label = String(node.label || "");

      if (label.toUpperCase().includes(search.toUpperCase())) {
        result.push({
          ...node,

          // ตอน search ไม่ต้องแสดง children
          children: [],

          depth: 0,
        });
      }

      if (node.children && node.children.length > 0) {
        walk(node.children);
      }
    }
  };

  walk(nodes);

  return result;
}

// =====================================================
// TreeViewTable
// =====================================================
export default function TreeViewTable({
  nodes,
  checked,
  setChecked,
  checkedData,
  setCheckedData,
  height = "500px",
  multi = false,
}) {
  const [userExpanded, setUserExpanded] = useState([]);

  const [search, setSearch] = useState("");

  const [searchInput, setSearchInput] = useState("");

  // ===================================================
  // Debounce search
  // ===================================================
  const debouncedSearch = useMemo(
    () =>
      _.debounce((value) => {
        setSearch(value);
      }, 200),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // ===================================================
  // Search change
  // ===================================================
  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearchInput(value);

    debouncedSearch(value);
  };

  // ===================================================
  // Filter
  // ===================================================
  const filtered = useMemo(() => {
    if (!search) {
      return nodes;
    }

    return searchNodes(nodes, search);
  }, [nodes, search]);

  // ===================================================
  // Expanded
  // ===================================================
  const expanded = userExpanded;

  // ===================================================
  // Flat data
  // ===================================================
  const flat = useMemo(() => {
    // ตอน search
    // แสดงเฉพาะรายการที่ค้นเจอ
    if (search) {
      return filtered.map((node) => ({
        ...node,
        depth: 0,
        children: [],
      }));
    }

    // ปกติแสดง tree
    return flatten(filtered, userExpanded);
  }, [filtered, userExpanded, search]);

  // ===================================================
  // Leaf ทั้งหมด
  // ===================================================
  const uniqueLeafs = useMemo(() => {
    return [...new Set(allLeafIds(nodes))];
  }, [nodes]);

  const allChecked =
    multi &&
    uniqueLeafs.length > 0 &&
    checked.length === uniqueLeafs.length &&
    uniqueLeafs.every((id) => checked.includes(id));

  const indeterminate =
    multi &&
    checked.length > 0 &&
    !allChecked;

  // ===================================================
  // Toggle expand / collapse
  // ===================================================
  const handleToggle = useCallback((node) => {
    setUserExpanded((prev) => {
      if (prev.includes(node.value)) {
        return prev.filter((v) => v !== node.value);
      }

      return [...prev, node.value];
    });
  }, []);

  // ===================================================
  // Check
  // ===================================================
  const handleCheck = useCallback(
    (node, newValue) => {
      const ids = leafIds(node);

      // =============================================
      // Single select
      // =============================================
      if (!multi) {
        if (
          node.children &&
          node.children.length > 0
        ) {
          return;
        }

        if (newValue) {
          setChecked([node.value]);
          setCheckedData([node]);
        } else {
          setChecked([]);
          setCheckedData([]);
        }

        return;
      }

      // =============================================
      // Leaf
      // =============================================
      if (
        !node.children ||
        node.children.length === 0
      ) {
        if (newValue) {
          // เพิ่ม + กันซ้ำ
          const newChecked = [
            ...new Set([
              ...checked,
              node.value,
            ]),
          ];

          // เพิ่ม + กันซ้ำด้วย value
          const newCheckedData = [
            ...new Map(
              [...checkedData, node].map(
                (item) => [
                  item.value,
                  item,
                ]
              )
            ).values(),
          ];

          setChecked(newChecked);
          setCheckedData(newCheckedData);
        } else {
          // =========================================
          // UNCHECK
          // ลบ value นี้ออกทั้งหมด
          // =========================================
          const newChecked =
            checked.filter(
              (id) =>
                id !== node.value
            );

          const newCheckedData =
            checkedData.filter(
              (item) =>
                item.value !==
                node.value
            );

          setChecked(newChecked);
          setCheckedData(
            newCheckedData
          );
        }

        return;
      }

      // =============================================
      // Parent
      // =============================================
      const remainingIds =
        checked.filter(
          (id) =>
            !ids.includes(id)
        );

      // =============================================
      // CHECK PARENT
      // =============================================
      if (newValue) {
        const newChecked = [
          ...new Set([
            ...remainingIds,
            ...ids,
          ]),
        ];

        const leafNodes = ids
          .map((id) =>
            findNodeById(
              nodes,
              id
            )
          )
          .filter(Boolean);

        const newCheckedData = [
          ...new Map(
            [
              ...checkedData.filter(
                (item) =>
                  !ids.includes(
                    item.value
                  )
              ),
              ...leafNodes,
            ].map((item) => [
              item.value,
              item,
            ])
          ).values(),
        ];

        setChecked(newChecked);
        setCheckedData(
          newCheckedData
        );
      }

      // =============================================
      // UNCHECK PARENT
      // =============================================
      else {
        const newCheckedData =
          checkedData.filter(
            (item) =>
              !ids.includes(
                item.value
              )
          );

        setChecked(
          remainingIds
        );

        setCheckedData(
          newCheckedData
        );
      }
    },
    [
      checked,
      checkedData,
      setChecked,
      setCheckedData,
      multi,
      nodes,
    ]
  );

  // ===================================================
  // Remove จากรายการที่เลือก
  // ===================================================
  const handleRemoveSelected = useCallback(
    (node) => {
      const newChecked = checked.filter(
        (id) => id !== node.value
      );

      const newCheckedData = checkedData.filter(
        (item) => item.value !== node.value
      );

      setChecked(newChecked);

      setCheckedData(newCheckedData);
    },
    [
      checked,
      checkedData,
      setChecked,
      setCheckedData,
    ]
  );

  // ===================================================
  // Render
  // ===================================================
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 2,
        width: "100%",
        height: "100%",
      }}
    >
      {/* LEFT */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          รายการทั้งหมด
        </Typography>

        <TextField
          placeholder="ค้นหา..."
          fullWidth
          size="small"
          value={searchInput}
          onChange={handleSearchChange}
        />

        {multi && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={(e) => {
                if (e.target.checked) {
                  const uniqueIds = [
                    ...new Set(allLeafIds(nodes)),
                  ];

                  const uniqueData = [
                    ...new Map(
                      uniqueIds
                        .map((id) => findNodeById(nodes, id))
                        .filter(Boolean)
                        .map((item) => [item.value, item])
                    ).values(),
                  ];

                  setChecked(uniqueIds);
                  setCheckedData(uniqueData);
                } else {
                  setChecked([]);
                  setCheckedData([]);
                }
              }}
            />
            <Typography fontSize="0.9rem">
              เลือกทั้งหมด
            </Typography>
          </Box>
        )}

        {/* LEFT LIST */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            mt: 1,
            border: "1px solid #ccc",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Virtuoso
            style={{ height: "100%" }}
            data={flat}
            itemContent={(index, node) => {
              const ids = leafIds(node);

              const isChecked = ids.every((id) =>
                checked.includes(id)
              );

              const isIndeterminate =
                !isChecked &&
                ids.some((id) => checked.includes(id));

              return (
                <Box
                  sx={{
                    pl: node.depth * 2,
                    pr: 1,
                    minHeight: "36px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {!search &&
                    node.children?.length > 0 && (
                      <Box
                        onClick={() => handleToggle(node)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {expanded.includes(node.value) ? (
                          <ArrowDropDownRoundedIcon />
                        ) : (
                          <ArrowRightRoundedIcon />
                        )}
                      </Box>
                    )}

                  <Checkbox
                    size="small"
                    checked={isChecked}
                    indeterminate={isIndeterminate}
                    disabled={
                      !multi &&
                      node.children?.length > 0
                    }
                    onChange={() =>
                      handleCheck(node, !isChecked)
                    }
                    sx={{ p: 0.5 }}
                  />

                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {node.label}
                  </Typography>
                </Box>
              );
            }}
          />
        </Box>
      </Box>

      {/* RIGHT */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          รายการที่เลือก ({checkedData.length})
        </Typography>

        {/* RIGHT LIST */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            border: "1px solid #ccc",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {checkedData.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              ยังไม่มีรายการที่เลือก
            </Box>
          ) : (
            <Virtuoso
              style={{ height: "100%" }}
              data={checkedData}
              itemContent={(index, node) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: "36px",
                    px: 1,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked
                    onChange={() =>
                      handleRemoveSelected(node)
                    }
                    sx={{ p: 0.5 }}
                  />

                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {node.label}
                  </Typography>
                </Box>
              )}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}